import { useEffect, useRef, useState } from 'react';

import type { RegisterColumn } from './applications-model';

import './applications.css';

/**
 * T.Z219 — the register's «Колонки» control: a quiet button in the filter bar
 * opening a checkbox popover, one entry per column of the table.
 *
 * NOT a filter: the state lives in `localStorage`, never in the URL, and the
 * T.Z192 «applied» tint conventions deliberately do NOT apply — hiding a column
 * narrows the VIEW, not the data, so the button stays visually quiet (the ghost
 * style the bar's «Сбросить» wears). The api still answers every field; this is
 * presentation only (see `./applications-columns` for the model and the storage
 * shape).
 *
 * At least one column must stay visible: the last enabled checkbox is disabled,
 * and the model refuses the same click if it ever reached it.
 */
export function RegisterColumnsControl({
  columns,
  visible,
  onToggle,
  onReset,
}: {
  /** Every column the current build renders, in table order (the popover's list). */
  columns: ReadonlyArray<RegisterColumn>;
  /** The ids the table shows right now (the checkbox states). */
  visible: ReadonlyArray<string>;
  /** One checkbox click (the model owns the never-all-hidden rule). */
  onToggle: (id: string) => void;
  /** «Сбросить» — every column back. */
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Click-outside and Escape close the popover — the same two gestures a native
  // `<select>` answers to, so the control never needs a scrim of its own.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const shown = new Set(visible);

  return (
    <div className="columns-control" ref={rootRef}>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        data-testid="columns-toggle"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((current) => !current)}
      >
        Колонки
      </button>
      {open ? (
        <div className="columns-control__panel" data-testid="columns-panel" role="group" aria-label="Колонки таблицы">
          <ul className="columns-control__list">
            {columns.map((column) => {
              // The last enabled checkbox cannot be unchecked: a table with no
              // columns is not a view. The model refuses it too — this disable is
              // the visible half of the same rule.
              const disabled = shown.has(column.id) && visible.length <= 1;
              return (
                <li key={column.id}>
                  <label className="columns-control__item">
                    <input
                      type="checkbox"
                      data-testid={`columns-item-${column.id}`}
                      checked={shown.has(column.id)}
                      disabled={disabled}
                      onChange={() => onToggle(column.id)}
                    />
                    <span>{column.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="columns-control__actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              data-testid="columns-reset"
              onClick={onReset}
            >
              Сбросить
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
